import {
  AutoComplete,
  AutoCompleteItem,
  AutoCompleteProps
} from "@fidget/common/components/form/autoComplete/autoComplete";
import { EditInput } from "@fidget/common/components/form/editInput";
import classNames from "classnames";
import Downshift, {
  ControllerStateAndHelpers,
  DownshiftState,
  StateChangeOptions
} from "downshift";
import { debounce } from "lodash";
import React, { useCallback, useState } from "react";
import { config } from "../../../config";
import { intl } from "../../../i18n";
import { logger } from "../../../utils";
import { SpinIcon } from "../../icons/spinIcon/spinIcon";
import styles from "./asyncAutoComplete.css";

type Props<T> = {
  itemsProvider: (query: string) => Promise<T[]>;
  debounceMillis?: number;
  /** Keep query string after selecting result or blurring input */
  keepQuery?: boolean;
  renderQueryInput?: (
    helpers: ControllerStateAndHelpers<T>,
    state: AsyncAutoCompleteState
  ) => JSX.Element;
} & Omit<AutoCompleteProps<T>, "itemsProvider" | "renderQueryInput">;

// dev note: created from old agent/GuestSearchInput
export const AsyncAutoComplete = function <T extends AutoCompleteItem>({
  itemsProvider,
  renderQueryInput,
  debounceMillis = config.services.times.inputDebounceMs,
  keepQuery,
  ...autoCompleteProps
}: Props<T>) {
  const [query, setQuery] = useState<string>("");
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [queryError, setQueryError] = useState<string>();

  const debouncedFetchItems = useCallback(
    debounce(
      async (
        q: string,
        setItemsCallback: (items: T[]) => void,
        setIsLoadingCallback: (isLoading: boolean) => void,
        itemsProviderCallback: (query: string) => Promise<T[]>,
        setQueryCallback: (q: string) => void,
        setQueryErrorCallback: (error: string) => void
      ) => {
        if (q !== query) {
          setIsLoadingCallback(true);
          try {
            const loadeditems = await itemsProviderCallback(q);
            setQueryCallback(q);
            setItemsCallback(loadeditems);
            setIsLoadingCallback(false);
          } catch (e) {
            logger.error({
              message: "Error occured while fetching items in auto complete",
              error: e
            });
            setQueryErrorCallback(intl.formatMessage("error.something"));
          }
        }
      },
      debounceMillis
    ),
    []
  );

  /**
   * Fetch from the backend and set the items, but not too often
   * @param {string} query  Query string
   */
  const fetchitems = (q: string) =>
    debouncedFetchItems(q, setItems, setIsLoading, itemsProvider, setQuery, setQueryError);

  /**
   * Handle Downshift input change event to fetch or reset results
   */
  const handleInputChange = (inputValue: string) => {
    if (inputValue) {
      fetchitems(inputValue);
    } else {
      setItems([]);
      setQuery("");
    }
  };

  /**
   * On query input focus, show the dropdown if the input contains text
   */
  const onInputFocus = (
    ev: React.FormEvent<HTMLInputElement>,
    helpers: ControllerStateAndHelpers<T>
  ) => {
    if (ev.currentTarget.value) {
      helpers.openMenu();
    }
  };

  /**
   * Use Downshift's internal state changes to prevent input value reset
   * on blur/select and close the menu when input value is emptied by user
   */
  const downshiftStateReducer = (
    state: DownshiftState<T>,
    changes: StateChangeOptions<T>
  ): Partial<StateChangeOptions<T>> => {
    const { keyDownEnter, clickItem, blurInput, mouseUp, changeInput } = Downshift.stateChangeTypes;
    switch (changes.type) {
      case mouseUp:
      case clickItem:
      case keyDownEnter:
      case blurInput:
        return { ...changes, isOpen: false };
      case changeInput:
        return { ...changes, isOpen: !!changes.inputValue };
      default:
        break;
    }
    return changes;
  };

  const itemToString = (item: T | null) => {
    if (keepQuery) {
      return query;
    }
    return item && autoCompleteProps.itemToString ? autoCompleteProps.itemToString(item) : "";
  };

  const renderInput =
    renderQueryInput ||
    ((helpers: ControllerStateAndHelpers<T>) => {
      const { getInputProps } = helpers;
      const { hasError, error, placeholder } = autoCompleteProps;
      const inputProps = getInputProps({
        hasError,
        errorHint: error,
        placeholder,
        onFocus: onInputFocus
      });
      return (
        <div className={styles["input-container"]}>
          <EditInput className={autoCompleteProps.inputContainerClassName} {...inputProps} />
          <SpinIcon hidden={!isLoading} />
        </div>
      );
    });

  const render = () => {
    const menuClassName = classNames(autoCompleteProps.menuClassName, {
      [styles["menu-hidden"]]: !items.length
    });

    return (
      <AutoComplete<T>
        {...autoCompleteProps}
        menuClassName={menuClassName}
        itemToString={itemToString}
        renderQueryInput={(helpers) => renderInput(helpers, { isEmpty: !items.length, isLoading })}
        stateReducer={downshiftStateReducer}
        itemsProvider={() => items}
        onInputValueChange={handleInputChange}
        error={queryError}
      />
    );
  };

  return render();
};

export interface AsyncAutoCompleteState {
  isLoading: boolean;
  isEmpty: boolean;
}
